import { Plus, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useController, useForm } from 'react-hook-form';

import {
  ANALOG_BANDWIDTH_OPTIONS,
  DIGITAL_BANDWIDTH_OPTIONS,
  FREQUENCY_STEP_MHZ,
} from '../../constants/suppressions.js';
import { useCreateBroadbandSuppression, useIntl } from '../../hooks';
import { resolveLazyTranslation } from '../../i18n/validation.js';
import { mhzToHz } from '../../lib/frequency.js';
import { zodResolver } from '../../lib/zod-resolver.js';
import { SignalType } from '../../types';
import {
  buildMultiFrequencyRulesSchema,
  buildMultiFrequencyValueSchema,
  buildMultiSuppressionSchema,
  MAX_MULTI_FREQUENCIES,
  type MultiSuppressionFormValues,
} from '../../validations/broadband-suppression.js';
import { Badge, Button, Input } from '../ui';
import { SignalTypeSelector } from './SignalTypeSelector.js';

const STEP_STANDING_TIME_OPTIONS = Array.from({ length: 20 }, (_, index) => (index + 1) * 5);

const selectClassName =
  'h-10 w-full rounded-md border border-border bg-background-light px-2 text-sm shadow-sm';

const FREQUENCY_INPUT_ID = 'multi-frequency-input';

type MultiSuppressionFormInput = Omit<MultiSuppressionFormValues, 'duration'> & {
  duration: string;
};

type MultiFrequencyPanelProps = {
  deviceId: string;
  defaultSuppressionTime: number | null;
  disabled: boolean;
  minAllowedMhz: number | null;
  maxAllowedMhz: number | null;
  maxSuppressionTime: number | null;
  defaultAnalogMultiFrequencyBandwidthMhz: number;
  defaultDigitalMultiFrequencyBandwidthMhz: number;
  defaultSignalType: SignalType;
  onCreated?: () => void;
};

type FieldProps = {
  label: string;
  htmlFor?: string;
  error?: string;
  className?: string;
  children: ReactNode;
};

function Field({ label, htmlFor, error, className, children }: FieldProps) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      {/* Truncate rather than the `whitespace-nowrap` this used to carry: nowrap on a column narrow
          enough to share one row lets "Step standing time (ms)" spill over its neighbour's label.
          `title` keeps the full wording reachable on hover. Mirrors JumperPanel's Field. */}
      <label
        htmlFor={htmlFor}
        title={label}
        className="block truncate text-xs font-medium text-foreground"
      >
        {label}
      </label>
      {children}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

export function MultiFrequencyPanel({
  deviceId,
  defaultSuppressionTime,
  disabled,
  minAllowedMhz,
  maxAllowedMhz,
  maxSuppressionTime,
  defaultAnalogMultiFrequencyBandwidthMhz,
  defaultDigitalMultiFrequencyBandwidthMhz,
  defaultSignalType,
  onCreated,
}: MultiFrequencyPanelProps) {
  const intl = useIntl();
  const createBroadbandSuppression = useCreateBroadbandSuppression();
  const defaultValues = useMemo<MultiSuppressionFormInput>(
    () => ({
      // Both seeded from the device's default type — the prop, never the watched form value: a
      // watched value churns this memo's identity on every switch, re-firing the `!isDirty`
      // re-seed below.
      type: defaultSignalType,
      frequencies: [],
      bandwidth:
        defaultSignalType === SignalType.DIGITAL
          ? defaultDigitalMultiFrequencyBandwidthMhz
          : defaultAnalogMultiFrequencyBandwidthMhz,
      stepStandingTimeMs: 10,
      duration: defaultSuppressionTime != null ? String(defaultSuppressionTime) : '',
    }),
    [
      defaultAnalogMultiFrequencyBandwidthMhz,
      defaultDigitalMultiFrequencyBandwidthMhz,
      defaultSignalType,
      defaultSuppressionTime,
    ],
  );
  const validationSchema = useMemo(
    () => buildMultiSuppressionSchema(minAllowedMhz, maxAllowedMhz, maxSuppressionTime),
    [maxAllowedMhz, maxSuppressionTime, minAllowedMhz],
  );
  const frequencyValueSchema = useMemo(
    () => buildMultiFrequencyValueSchema(minAllowedMhz, maxAllowedMhz),
    [maxAllowedMhz, minAllowedMhz],
  );
  const frequencyRulesSchema = useMemo(
    () => buildMultiFrequencyRulesSchema(minAllowedMhz, maxAllowedMhz),
    [maxAllowedMhz, minAllowedMhz],
  );
  const {
    clearErrors,
    control,
    formState: { errors, isDirty, isSubmitting, isValid },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<MultiSuppressionFormInput>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });
  const {
    field: { onChange: onFrequenciesChange, value: frequencies },
  } = useController({ control, name: 'frequencies' });

  const [frequencyDraft, setFrequencyDraft] = useState('');
  const [frequencyDraftError, setFrequencyDraftError] = useState<string | null>(null);
  const frequencyInputRef = useRef<HTMLInputElement | null>(null);
  const shouldRefocusInputRef = useRef(false);

  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues, { keepErrors: true });
    }
  }, [defaultValues, isDirty, reset]);

  const selectedType = watch('type');
  const selectedBandwidth = watch('bandwidth');
  const bandwidthOptions =
    selectedType === SignalType.DIGITAL ? DIGITAL_BANDWIDTH_OPTIONS : ANALOG_BANDWIDTH_OPTIONS;
  const previousTypeRef = useRef(selectedType);

  useEffect(() => {
    const defaultBandwidth =
      selectedType === SignalType.DIGITAL
        ? defaultDigitalMultiFrequencyBandwidthMhz
        : defaultAnalogMultiFrequencyBandwidthMhz;
    const didTypeChange = previousTypeRef.current !== selectedType;

    previousTypeRef.current = selectedType;

    if (didTypeChange || !bandwidthOptions.includes(Number(selectedBandwidth))) {
      // `shouldDirty` compares the written value against the CURRENT defaults, so this dirties the
      // form only when the operator switched type: after a re-seed the width being written is
      // already the freshly seeded default, leaving the form pristine for the next re-seed.
      setValue('bandwidth', defaultBandwidth, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    bandwidthOptions,
    defaultAnalogMultiFrequencyBandwidthMhz,
    defaultDigitalMultiFrequencyBandwidthMhz,
    selectedBandwidth,
    selectedType,
    setValue,
  ]);

  // The button that was just clicked unmounts with its badge. Left alone, focus falls to `<body>`
  // and Radix's FocusScope pulls it up to the dialog container — outside this section, which is
  // what the Detections dialog's Escape exemption keys off, so the next Escape would close the
  // dialog and discard the remaining frequencies.
  //
  // After commit, not inside the click handler: at MAX_MULTI_FREQUENCIES the input is still
  // `disabled` when the handler runs, and `focus()` on a disabled input is a no-op — the removal
  // that drops the list back under the cap is exactly the one that needs this most.
  useEffect(() => {
    if (!shouldRefocusInputRef.current) return;
    shouldRefocusInputRef.current = false;
    frequencyInputRef.current?.focus();
  }, [frequencies]);

  const signalTypeLabel = intl.formatMessage({
    id: 'broadbandSuppressionsDialog.form.signalType',
  });
  const isFieldDisabled = disabled || isSubmitting || createBroadbandSuppression.isPending;
  const isActionDisabled = isFieldDisabled || !isValid;
  const isFrequencyEntryDisabled = isFieldDisabled || frequencies.length >= MAX_MULTI_FREQUENCIES;
  // One batch, one signal type: every committed frequency was entered under the type showing at
  // the time, so the selector locks with the first badge and unlocks only when the last one is
  // removed. Without the lock a switch after entry would silently re-type the whole list, and
  // reset the width under it (see the effect above).
  const isSignalTypeLocked = isFieldDisabled || frequencies.length > 0;
  const frequencyError = frequencyDraftError ?? errors.frequencies?.message;

  const addFrequency = () => {
    const parsedValue = frequencyValueSchema.safeParse(frequencyDraft);
    if (!parsedValue.success) {
      setFrequencyDraftError(resolveLazyTranslation(parsedValue.error.issues[0].message));
      return;
    }

    const parsedList = frequencyRulesSchema.safeParse([...frequencies, parsedValue.data]);
    if (!parsedList.success) {
      setFrequencyDraftError(resolveLazyTranslation(parsedList.error.issues[0].message));
      return;
    }

    // Chips always render ascending regardless of entry order.
    onFrequenciesChange([...parsedList.data].sort((a, b) => a - b));
    setFrequencyDraft('');
    setFrequencyDraftError(null);
  };

  const removeFrequency = (removedIndex: number) => {
    onFrequenciesChange(frequencies.filter((_frequency, index) => index !== removedIndex));
    setFrequencyDraftError(null);
    shouldRefocusInputRef.current = true;
  };

  const handleFrequencyKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    addFrequency();
  };

  const onSubmit = handleSubmit(async (values) => {
    clearErrors('root');

    try {
      await createBroadbandSuppression.mutateAsync({
        deviceId,
        payload: {
          type: values.type,
          mode: 'multi',
          channelFrequencyList: values.frequencies.map(mhzToHz).sort((a, b) => a - b),
          bandwidth: mhzToHz(values.bandwidth),
          stepStandingTimeMs: values.stepStandingTimeMs,
          duration: Number(values.duration),
        },
      });
      reset(defaultValues);
      setFrequencyDraft('');
      setFrequencyDraftError(null);
      onCreated?.();
    } catch (error) {
      setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.genericSubmitError' }),
      });
    }
  });

  return (
    <form
      className="flex w-full flex-col gap-2"
      data-testid="multi-suppression-form"
      onSubmit={onSubmit}
    >
      {/* Every field on one row, like the sibling suppression tabs. `flex-1` with `min-w-*` floors
          so the four value inputs divide the section's full width — the signal-type selector is
          excluded, being a row of fixed-size icon toggles rather than a value input, so stretching
          it would only pad it with dead space. The floors are what `flex-wrap`
          breaks on, so a narrow viewport spills onto a second line rather than squeezing the number
          inputs. The frequency badges stay below the row — they appear and disappear as the
          operator types, and inline they would resize the row under the cursor. */}
      <div className="flex flex-wrap items-end gap-2">
        <Field label={signalTypeLabel} className="shrink-0">
          <SignalTypeSelector
            groupLabel={signalTypeLabel}
            name="multi-type"
            value={selectedType}
            disabled={isSignalTypeLocked}
            onChange={(type) => setValue('type', type, { shouldDirty: true, shouldValidate: true })}
          />
        </Field>
        <Field
          label={intl.formatMessage({
            id: 'broadbandSuppressionsDialog.form.multi.frequency',
          })}
          htmlFor={FREQUENCY_INPUT_ID}
          className="min-w-40 flex-1"
        >
          <Input
            id={FREQUENCY_INPUT_ID}
            type="number"
            // Every control in a form gates its submit event, this draft input included.
            step={FREQUENCY_STEP_MHZ}
            suffix="MHz"
            data-testid={FREQUENCY_INPUT_ID}
            placeholder={intl.formatMessage({
              id: 'broadbandSuppressionsDialog.form.multi.frequencyPlaceholder',
            })}
            ref={frequencyInputRef}
            disabled={isFrequencyEntryDisabled}
            value={frequencyDraft}
            onChange={(event) => {
              setFrequencyDraft(event.target.value);
              setFrequencyDraftError(null);
            }}
            onKeyDown={handleFrequencyKeyDown}
            className={frequencyError ? 'border-destructive' : undefined}
          />
          {/* Under its own input rather than at the foot of the form: a rejected entry is a field
              error about the value still sitting in that box, and several fields away it reads as
              a form-level failure. */}
          {frequencyError && (
            <span className="text-xs text-destructive" data-testid="multi-frequency-error">
              {frequencyError}
            </span>
          )}
        </Field>
        <Button
          type="button"
          variant="outline"
          className="shrink-0"
          data-testid="multi-add-frequency"
          disabled={isFrequencyEntryDisabled}
          onClick={addFrequency}
        >
          <Plus className="mr-2 h-4 w-4" />
          {intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.multi.addFrequency' })}
        </Button>
        <Field
          label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.bandwidth' })}
          htmlFor="multi-bandwidth"
          error={errors.bandwidth?.message}
          className="min-w-20 flex-1"
        >
          <select
            id="multi-bandwidth"
            data-testid="multi-bandwidth"
            disabled={isFieldDisabled}
            className={selectClassName}
            {...register('bandwidth', { valueAsNumber: true })}
          >
            {bandwidthOptions.map((value) => (
              <option key={`multi-bandwidth-${value}`} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={intl.formatMessage({
            id: 'broadbandSuppressionsDialog.form.stepStandingTime',
          })}
          htmlFor="multi-step-standing-time"
          error={errors.stepStandingTimeMs?.message}
          className="min-w-28 flex-1"
        >
          <select
            id="multi-step-standing-time"
            data-testid="multi-step-standing-time"
            disabled={isFieldDisabled}
            className={selectClassName}
            {...register('stepStandingTimeMs', { valueAsNumber: true })}
          >
            {STEP_STANDING_TIME_OPTIONS.map((value) => (
              <option key={`multi-step-standing-${value}`} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.duration' })}
          htmlFor="multi-duration"
          error={errors.duration?.message}
          className="min-w-24 flex-1"
        >
          <Input
            id="multi-duration"
            type="number"
            min={1}
            max={maxSuppressionTime ?? undefined}
            suffix="s"
            data-testid="multi-duration"
            disabled={isFieldDisabled}
            {...register('duration')}
            onFocus={(event) => event.target.select()}
            className={errors.duration ? 'border-destructive' : undefined}
          />
        </Field>
      </div>

      {/* Submit trails the badge list, and never precedes it. Tabbing forward from the frequency
          input has to reach a badge's remove control before the destructive button that fires the
          jammer — with the button interposed, the undo affordance for a mistyped frequency sits
          past the irreversible action. */}
      <div className="flex flex-wrap items-center gap-2">
        {frequencies.length === 0 ? (
          <span className="flex-1 text-xs text-muted-foreground">
            {intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.multi.noFrequencies' })}
          </span>
        ) : (
          <div className="flex max-h-24 flex-1 flex-wrap gap-2 overflow-y-auto">
            {frequencies.map((frequency, index) => (
              <Badge
                key={frequency}
                variant="secondary"
                className="flex items-center gap-1 pr-1 text-xs"
                data-testid="multi-frequency-badge"
              >
                <span>
                  {intl.formatMessage(
                    { id: 'broadbandSuppressionsDialog.form.multi.frequencyBadge' },
                    { value: frequency },
                  )}
                </span>
                <button
                  type="button"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-border"
                  data-testid={`multi-remove-frequency-${index}`}
                  aria-label={intl.formatMessage(
                    { id: 'broadbandSuppressionsDialog.form.multi.removeFrequencyValue' },
                    { value: frequency },
                  )}
                  disabled={isFieldDisabled}
                  onClick={() => removeFrequency(index)}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        <Button
          type="submit"
          variant="destructive"
          size="sm"
          className="shrink-0"
          data-testid="multi-submit"
          disabled={isActionDisabled}
        >
          {createBroadbandSuppression.isPending
            ? intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.genericCreating' })
            : intl.formatMessage({ id: 'broadbandSuppressionsDialog.actions.suppress' })}
        </Button>

        {/* Basis-full so a server error takes its own line under the row rather than squeezing it. */}
        {errors.root && (
          <span className="basis-full text-xs text-destructive">{errors.root.message}</span>
        )}
      </div>
    </form>
  );
}
